<template>
  <div class="calendar-page q-pa-md">
    <div class="flex justify-end items-center q-pb-md">
      <q-btn
        icon="list"
        color="primary"
        :label="$t('bookings.goToBookings')"
        class="header-button"
        @click="goToList"
      />
    </div>

    <div v-if="isLoading" class="q-pa-xl text-center">
      <q-spinner color="primary" size="3em" />
      <div class="q-mt-md">{{ $t("bookings.loading") }}</div>
    </div>

    <div v-else-if="allBookings.length === 0" class="q-my-lg">
      <q-banner rounded class="bg-blue-1 text-blue-9">
        {{ $t("bookings.noActiveBookings") }}
      </q-banner>
    </div>

    <q-card v-else flat bordered class="calendar-card">
      <div class="calendar-header q-pa-md bg-grey-2">
        <div class="calendar-header__nav">
          <q-btn icon="chevron_left" flat round color="primary" @click="onPrev" />
          <div class="text-h5 text-weight-medium text-primary calendar-header__title">
            {{ currentMonthName }} {{ currentYear }}
          </div>
          <q-btn
            icon="chevron_right"
            flat
            round
            color="primary"
            @click="onNext"
          />
        </div>
        <div class="calendar-header__filters">
          <q-checkbox
            v-model="filters.bookings"
            :label="$t('bookings.bookingsTitle')"
            color="primary"
            dense
          />
          <q-checkbox
            v-model="filters.events"
            :label="$t('bookings.eventsTitle')"
            color="secondary"
            dense
          />
        </div>
      </div>

      <div class="calendar-wrapper">
        <q-calendar-month
          ref="calendar"
          v-model="selectedDate"
          :locale="$i18n.locale"
          animated
          bordered
          no-active-date
        >
          <template #day="{ scope }">
            <div
              class="calendar-day-cell full-height"
              :class="{ 'is-today': isToday(scope.timestamp) }"
            >
              <div
                class="day-number q-mb-xs"
                :class="{ 'today-number': isToday(scope.timestamp) }"
              >
                {{ scope.timestamp.day }}
              </div>
              <div class="calendar-items-container">
                <div v-if="filters.events" class="events-section q-mb-xs">
                  <div
                    v-for="event in getDayItems(scope.timestamp, 'event')"
                    :key="event.id"
                    class="calendar-item event-item ellipsis"
                    @click="handleItemClick(event)"
                  >
                    <q-icon name="public" size="10px" class="q-mr-xs" />
                    <span>{{ event.title }}</span>
                  </div>
                </div>

                <div v-if="filters.bookings" class="bookings-section">
                  <div
                    v-for="booking in getDayItems(scope.timestamp, 'booking')"
                    :key="booking.id"
                    class="calendar-item ellipsis text-white"
                    :class="`bg-${booking.color}`"
                    :style="{ backgroundColor: booking.bgcolor }"
                    @click="handleItemClick(booking)"
                  >
                    <q-icon name="event" size="10px" class="q-mr-xs" />
                    <span>{{ booking.title }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </q-calendar-month>
      </div>

      <div class="flex justify-center q-pa-md">
        <q-btn
          color="secondary"
          icon="keyboard_arrow_up"
          :label="$t('bookings.today')"
          @click="goToToday"
        />
      </div>
    </q-card>
  </div>
</template>

<script>
import { QCalendarMonth } from '@quasar/quasar-ui-qcalendar';
import '@quasar/quasar-ui-qcalendar/dist/index.css';
import { date } from 'quasar';
import BookingsMixin from '@/pages/bookings/mixins/BookingsMixin';
import { mapActions, mapState, mapGetters } from 'vuex';

export default {
  name: 'BookingsCalendar',
  components: {
    QCalendarMonth,
  },
  mixins: [BookingsMixin],
  data() {
    return {
      selectedDate: date.formatDate(Date.now(), 'YYYY-MM-DD'),
      filters: {
        bookings: true,
        events: true,
      },
    };
  },
  computed: {
    ...mapGetters('bookings', {
      allBookings: 'allBookings',
      isLoadingBookings: 'isLoading',
    }),
    ...mapState('global', {
      allEvents: 'eventsList',
      isLoadingEvents: 'isEventListLoading',
      citiesList: 'citiesList',
    }),
    isLoading() {
      return this.isLoadingBookings || this.isLoadingEvents;
    },
    currentYear() {
      return parseInt(this.selectedDate.split('-')[0]);
    },
    currentMonth() {
      return parseInt(this.selectedDate.split('-')[1]) - 1;
    },
    currentMonthName() {
      return this.$q.lang.date.months[this.currentMonth];
    },
    calendarItems() {
      const bookingsData = this.allBookings || [];
      const eventsData = this.allEvents || [];

      const bookings = bookingsData.map((booking) => ({
        id: `booking-${booking.id}`,
        title: this.displayNameOrUsernameFallback(booking.organizer),
        date: booking.date,
        color: this.getStatusColor(booking.status),
        type: 'booking',
        originalData: booking,
      }));

      const events = eventsData.map((event) => {
        let formattedDate = '';

        if (event.gear_date) {
          const dateStr = String(event.gear_date);
          const y = dateStr.substring(0, 4);
          const m = dateStr.substring(4, 6);
          const d = dateStr.substring(6, 8);
          formattedDate = `${y}-${m}-${d}`;
        }

        return {
          id: `event-${event.id}`,
          title: event.post_title,
          location: this.getEventLocation(event),
          date: formattedDate,
          bgcolor: '#9c27b0',
          type: 'event',
          originalData: event,
        };
      });

      return [...bookings, ...events];
    },
  },
  methods: {
    ...mapActions('bookings', ['fetchAllBookings']),
    ...mapActions('global', ['getEvents', 'getCities']),
    displayNameOrUsernameFallback(user) {
      const first = user.first_name?.trim();
      const last = user.last_name?.trim();

      if (first || last) {
        return `${first || ''} ${last || ''}`.trim();
      }

      return user.name;
    },
    getEventLocation(event) {
      const cityId = event.assign_city;
      const city = this.citiesList?.find((item) => String(item.id) === String(cityId));

      return city?.label || null;
    },
    getDayItems(timestamp, type) {
      if (!this.filters.bookings && type === 'booking') return [];
      if (!this.filters.events && type === 'event') return [];

      const dayDate = `${timestamp.year}-${String(timestamp.month).padStart(
        2,
        '0'
      )}-${String(timestamp.day).padStart(2, '0')}`;

      return this.calendarItems.filter((item) => {
        if (!item.date) return false;

        const itemDate = item.date.split('T')[0].replace(/\//g, '-');
        return itemDate === dayDate && item.type === type;
      });
    },
    handleItemClick(item) {
      if (item.type === 'booking') {
        this.showBookingDetails(item.originalData);
      } else {
        this.showEventDetails(item.originalData);
      }
    },
    showEventDetails(event) {
      const eventItem = this.calendarItems.find((i) => i.id === `event-${event.id}`);
      const eventDate = eventItem?.date ? this.formatDate(eventItem.date) : '-';
      const eventLocation = eventItem?.location || '-';

      this.$q.dialog({
        title: event.post_title,
        message: `
          <div>
            <strong>
              ${this.$t('common.date')}:
            </strong>
            ${eventDate}
          </div>
          <div>
            <strong>
              ${this.$t('events.location')}:
            </strong>
            ${eventLocation}
          </div>
        `,
        html: true,
      });
    },
    isToday(timestamp) {
      const today = new Date();
      return (
        timestamp.year === today.getFullYear() &&
        timestamp.month === today.getMonth() + 1 &&
        timestamp.day === today.getDate()
      );
    },
    onPrev() {
      this.$refs.calendar.prev();
    },
    onNext() {
      this.$refs.calendar.next();
    },
    goToToday() {
      this.selectedDate = date.formatDate(Date.now(), 'YYYY-MM-DD');
    },
    goToList() {
      this.$router.push({ name: 'user-bookings' });
    },
    showBookingDetails(booking) {
      this.$q.dialog({
        title: `${this.$t(
          'bookings.fromOrganizer'
        )} ${this.displayNameOrUsernameFallback(booking.organizer)}`,
        message: `
          <div>
            <strong>
              ${this.$t('bookings.bookingDate')}:
            </strong>
            ${this.formatDate(booking.date)}
          </div>
          <div>
            <strong>
              ${this.$t('bookings.statusLabel')}:
            </strong>
            ${this.getStatusLabel(booking.status)}
          </div>
          <div class="q-mt-md">
            <strong>
              ${this.$t('bookings.detailsMessage')}:
            </strong>
          </div>
          <div>
            ${booking.message || this.$t('bookings.noAdditionalMessage')}
          </div>
        `,
        html: true,
      });
    },
  },

  mounted() {
    if (!this.allBookings.length) {
      this.fetchAllBookings();
    }
  },
  async created() {
    await this.getEvents();
    await this.getCities();
  },
};
</script>

<style lang="scss" scoped>
.calendar-page {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  overflow-x: hidden;

  .calendar-card {
    width: 100%;
    overflow: hidden;
  }

  .calendar-header {
    border-bottom: 1px solid #e0e0e0;

    &__nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    &__title {
      text-align: center;
      line-height: 1.2;
      flex: 1;
      font-size: 34px;
    }

    &__filters {
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      flex-wrap: wrap;
    }
  }

  .calendar-wrapper {
    width: 100%;
    overflow: hidden;

    :deep(.q-calendar-month) {
      .q-calendar-month__week .q-calendar-month__day {
        min-height: 100px;
      }
      .q-calendar-month__day--label__wrapper {
        display: none;
      }
    }
  }

  .calendar-day-cell {
    padding: 4px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    &.is-today {
      background-color: #e3f2fd;
    }

    .day-number {
      font-size: 13px;
      font-weight: 500;
      color: #333;
      flex-shrink: 0;

      &.today-number {
        background-color: $secondary;
        color: white;
        border-radius: 50%;
        width: 26px;
        height: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
      }
    }
  }

  .calendar-items-container {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    flex-grow: 1;
    overflow-y: auto;
  }

  .calendar-item {
    border-radius: 4px;
    padding: 3px 6px;
    font-size: 10px;
    line-height: 1.4;
    font-weight: 700;
    cursor: pointer;
    color: white;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.85;
    }

    &.event-item {
      background: #f3e5f5;
      border: 1px solid #9c27b0;
      color: #9c27b0;
    }
  }
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .calendar-page {
    padding: 8px;

    .calendar-header {
      padding: 8px;

      &__title {
        font-size: 28px;
      }

      &__filters {
        margin-top: 4px;
        gap: 4px;
      }
    }

    .calendar-wrapper {
      :deep(.q-calendar-month) {
        .q-calendar-month__week .q-calendar-month__day {
          min-height: 80px;
        }

        .q-calendar-month__head-weekday-label {
          font-size: 11px;
        }
      }
    }

    .calendar-item {
      font-size: 9px;
      padding: 2px 4px;
    }
  }
}
</style>
